// Grant 2026 attribution — mandatory statement, partner logos, and the always-dark
// footer band. Single source of truth for the statement text (SQ mandatory, EN translation).
import { Fragment } from "react"
import { Link } from "react-router-dom"
import { ArrowRight } from "lucide-react"

export type GrantLang = "sq" | "en"

export const GRANT_PAGE_PATH = "/grant-2026"

/** Design tokens for the always-dark grant surfaces (hard-coded on purpose: they must not follow the theme). */
export const GRANT_INK = "#0A0A0F"
export const GRANT_PAPER = "#F5F3EE"
export const GRANT_RED = "#D93A2E"

export type StatementToken = { text: string; strong: boolean }

/** Builds the statement word by word. Plain strings are connective text; single-item arrays name an institution (emphasised). */
function words(...parts: (string | [string])[]): StatementToken[] {
  return parts.flatMap((part) =>
    Array.isArray(part)
      ? part[0].split(" ").map((text): StatementToken => ({ text, strong: true }))
      : part.split(" ").map((text): StatementToken => ({ text, strong: false })),
  )
}

export const GRANT_LOGOS = [
  {
    key: "ministry",
    src: "/grant/ministria-ekonomise-inovacionit.png",
    name: { sq: "Ministria e Ekonomisë dhe Inovacionit", en: "Ministry of Economy and Innovation" },
  },
  {
    key: "startupAlbania",
    src: "/grant/startup-albania.png",
    name: { sq: "Startup Albania", en: "Startup Albania" },
  },
] as const

export const GRANT_TEXT: Record<GrantLang, { supportedBy: string; statement: StatementToken[]; learnMore: string }> = {
  sq: {
    supportedBy: "Mbështetur nga",
    statement: words(
      "Platforma", ["The Not Fit"],
      "realizohet me mbështetjen financiare të", ["Qeverisë Shqiptare"],
      "përmes", ["Ministrisë së Ekonomisë dhe Inovacionit,"],
      "në kuadër të skemës", ["Grant 2026,"],
      "dhe zbatohet nga", ["Agjencia Innovation4Albania."],
    ),
    learnMore: "Mëso më shumë për Grant 2026",
  },
  en: {
    supportedBy: "Supported by",
    statement: words(
      ["The Not Fit"], "platform is realized with the financial support of the", ["Albanian Government"],
      "through the", ["Ministry of Economy and Innovation,"],
      "within the framework of the", ["Grant 2026"],
      "scheme, and is implemented by the", ["Innovation4Albania Agency."],
    ),
    learnMore: "Learn more about Grant 2026",
  },
}

/** Any of the landing pages' language codes → the two languages the statement exists in. */
export function toGrantLang(lang?: string | null): GrantLang {
  return lang === "sq" ? "sq" : "en"
}

/** The statement as a plain sentence (e.g. for meta descriptions). */
export function statementText(lang: GrantLang): string {
  return GRANT_TEXT[lang].statement.map((token) => token.text).join(" ")
}

export function StatementText({ tokens, strongClassName }: { tokens: StatementToken[]; strongClassName: string }) {
  return (
    <>
      {tokens.map((token, i) => (
        <Fragment key={i}>
          {token.strong ? <span className={strongClassName}>{token.text}</span> : token.text}
          {i < tokens.length - 1 ? " " : null}
        </Fragment>
      ))}
    </>
  )
}

/** Full-width dark strip for page footers. Stays dark in both themes. */
export function GrantFooterBand({ lang }: { lang?: string | null }) {
  const grantLang = toGrantLang(lang)
  const t = GRANT_TEXT[grantLang]
  const [ministry, startupAlbania] = GRANT_LOGOS

  return (
    <div lang={grantLang} className="border-t border-white/10 bg-[#0A0A0F] text-[#F5F3EE]">
      <div className="container mx-auto px-4 py-10">
        <div className="grid gap-8 lg:grid-cols-[auto_1fr] lg:items-center lg:gap-14">
          <div>
            <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F5F3EE]/45">{t.supportedBy}</p>
            <div className="flex items-center gap-7">
              <img src={ministry.src} alt={ministry.name[grantLang]} className="h-20 w-auto" />
              <div aria-hidden className="h-12 w-px bg-white/15" />
              <img src={startupAlbania.src} alt={startupAlbania.name[grantLang]} className="h-11 w-auto" />
            </div>
          </div>

          <div className="max-w-2xl">
            <p className="text-sm leading-relaxed text-[#F5F3EE]/55">
              <StatementText tokens={t.statement} strongClassName="text-[#F5F3EE]" />
            </p>
            <Link
              to={GRANT_PAGE_PATH}
              className="mt-3 inline-flex items-center gap-1.5 rounded text-sm font-medium text-[#F5F3EE] transition-colors hover:text-[#E8574B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8574B]/60"
            >
              {t.learnMore}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
